import { useScaffold } from "@Light/scaffold";
import {
  Input as HeadlessInput,
  type InputProps as HeadlessInputProps,
  Select as HeadlessSelect,
  type SelectProps as HeadlessSelectProps,
} from "@headlessui/react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { ChevronDown } from "@untitledui/icons";
import { clsx } from "clsx";
import React from "react";
import { twMerge } from "tailwind-merge";

function fieldBorderStyle({
  isError,
  disabled,
}: {
  isError?: boolean;
  disabled?: boolean;
}) {
  if (disabled) {
    return "rounded-md border border-disabled bg-disabled_subtle shadow-xs";
  }
  return clsx(
    "rounded-md border bg-primary shadow-xs",
    isError
      ? "border-error focus-within:ring-4 focus-within:ring-focus-ring-error/24"
      : "border-secondary focus-within:border-brand focus-within:ring-4 focus-within:ring-focus-ring/24",
  );
}

const unstyledControl = clsx(
  "w-full min-w-0 appearance-none border-0 bg-none bg-transparent shadow-none",
  "text-md font-regular text-primary",
  "focus:border-0 focus:outline-none focus:ring-0",
  "disabled:text-disabled",
);

export const unstyledInputClassName = clsx(
  unstyledControl,
  "p-0 placeholder:font-normal placeholder:text-placeholder",
);

export type InputProps = Omit<HeadlessInputProps, "className"> & {
  className?: string;
  isError?: boolean;
  isLoading?: boolean;
  inputButton?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  /** Override default <input /> component with a custom component. */
  inputComponent?: React.ElementType;
};
export const Input: React.FC<InputProps> = React.forwardRef<
  HTMLInputElement,
  InputProps
>(
  (
    {
      className,
      isError,
      isLoading,
      inputButton,
      icon: Icon,
      inputComponent: CustomInput,
      ...props
    },
    ref,
  ) => {
    const scaffold = useScaffold();
    const Spinner = scaffold.system.spinner;
    const { type, disabled } = props;

    const InputComponent = CustomInput ?? HeadlessInput;
    // Headless UI's own input reads `invalid` and exposes it as aria-invalid.
    // A caller supplying its own control wires that up from the control's own
    // parent instead — ComboboxInput takes it from <Combobox>.
    const invalidProps = CustomInput ? {} : { invalid: isError };

    if (type === "checkbox" || type === "radio") {
      return (
        <InputComponent
          ref={ref}
          data-slot="control"
          {...invalidProps}
          className={twMerge(
            "border",
            isError ? "border-error" : "border-primary",
            type === "checkbox" && "rounded-xs",
            className,
          )}
          {...props}
        />
      );
    }

    return (
      <div className="flex w-full flex-row">
        <label
          className={clsx(
            "flex min-w-0 flex-1 cursor-text items-center gap-2 px-3.25 py-2.25",
            fieldBorderStyle({ isError, disabled }),
            inputButton && "rounded-r-none",
          )}
        >
          {Icon && <Icon className="size-5 shrink-0 text-fg-quaternary" />}
          <InputComponent
            ref={ref}
            data-slot="control"
            {...invalidProps}
            className={twMerge(unstyledInputClassName, className)}
            {...props}
          />
          {isError && (
            <ExclamationCircleIcon className="size-5 shrink-0 text-fg-error-secondary" />
          )}
          {isLoading && <Spinner className="size-5 shrink-0" />}
        </label>
        {inputButton}
      </div>
    );
  },
);

export type SelectProps = Omit<HeadlessSelectProps, "className"> & {
  className?: string;
  isError?: boolean;
};

export const Select: React.FC<SelectProps> = React.forwardRef<
  HTMLSelectElement,
  SelectProps
>(({ isError, className, ...props }, ref) => {
  return (
    <div
      data-slot="control"
      className={clsx(
        "relative flex w-full items-center",
        fieldBorderStyle({ isError, disabled: props.disabled }),
      )}
    >
      <HeadlessSelect
        ref={ref}
        invalid={isError}
        className={twMerge(
          unstyledControl,
          "py-2.25 pl-3.25 pr-8 font-medium",
          "has-[option[value='']:checked]:font-normal has-[option[value='']:checked]:text-placeholder",
          className,
        )}
        {...props}
      />
      <ChevronDown className="pointer-events-none absolute right-2.5 size-5 text-fg-quaternary" />
    </div>
  );
});
